'use client'

import React, { FC, HTMLAttributes, useCallback, useState } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { generatePath } from '@utils/path.utils'
import { NavLink } from '@components/shared/nav-link/NavLink'
import { cn } from '@utils'
import { NavItem } from '../model'
import { clsx } from 'clsx'
import { paths } from '@router'
import { ArrowRight2LightIcon } from '@components/icons/light'
import { ResponsiveIcon } from '@components/shared/responsiveIcon/ResponsiveIcon'
import { useCurrentBreakpoint } from '@hooks/useCurrentBreakpoint.hook'
import { useIsAuthenticated } from '@domain/auth/Auth.context'
import { supportDeskConfig } from '@config/supportDesk.config'
interface NavProps extends HTMLAttributes<HTMLDivElement> {
  landingPage?: boolean
  headerOpacity?: number
}

const getNavItems = (isAuthenticated: boolean): NavItem[] => {
  const items: NavItem[] = [
    {
      navLinkProps: { to: generatePath(paths.funds.exclusiveAccess.companies) },
      IconPath: '/images/wlth/InvestIconMobile.webp',
      label: 'Invest',
    },
    {
      // The marketplace bundle (Web3 providers) is ~4MB; don't prefetch it from every
      // page's header. Next still prefetches on hover/touch so navigation stays quick.
      navLinkProps: { to: generatePath(paths.marketplace.main), prefetch: false },
      IconPath: '/images/wlth/PortfolioIconMobile.webp',
      label: 'Marketplace',
      activeOn: [paths.marketplace.main],
    },
    {
      navLinkProps: { to: generatePath(paths.news) },
      IconPath: '/images/wlth/blog-icon.webp',
      label: 'News',
    },
  ]

  if (isAuthenticated || supportDeskConfig.enabled) {
    items.push({
      navLinkProps: {
        to: paths.support,
      },
      label: 'Support',
    })
  }

  return items
}

export const Nav: FC<NavProps> = ({
  className,
  landingPage = false,
  headerOpacity,
  ...props
}) => {
  const isAuthenticated = useIsAuthenticated()

  return (
    <div
      className={cn(
        'flex md:w-full gap-1 sm:gap-5 lg:gap-7 items-center justify-center px-4 sm:px-6',
        className,
      )}
      {...props}
    >
      {getNavItems(isAuthenticated)
        .filter((item) => item.enabled !== false)
        .map((item, index) => (
          <NavMenuItemEl
            key={index}
            {...item}
            landingPage={landingPage}
            headerOpacity={headerOpacity}
          />
        ))}
    </div>
  )
}

export const NavMenuItemEl: FC<
  NavItem & { landingPage?: boolean; headerOpacity?: number }
> = ({
  label,
  Icon,
  IconPath,
  navLinkProps,
  isDisabled = false,
  activeOn = [],
  subItems = [],
  landingPage = false,
  headerOpacity,
  iconWidth,
  iconHeight,
}) => {
  const currentPath = usePathname()
  const router = useRouter()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const hasDropdown = subItems && subItems.length > 0
  const { isMobile } = useCurrentBreakpoint()

  const getTextColor = () => {
    if (!landingPage || headerOpacity === undefined) {
      return undefined // Use default color
    }

    // Interpolate between white (#ffffff) and Midnight Purple (#211F44 = rgb(33, 31, 68))
    const r = Math.round(255 - (255 - 33) * headerOpacity)
    const g = Math.round(255 - (255 - 31) * headerOpacity)
    const b = Math.round(255 - (255 - 68) * headerOpacity)

    return `rgb(${r}, ${g}, ${b})`
  }

  const textColor = getTextColor()

  const shouldBeHighlighted = useCallback(
    (isActive: boolean) => {
      // Never highlight chatbot as active since it's an action, not a page
      if (label === 'Chatbot') {
        return false
      }
      return isActive || activeOn.some((p) => currentPath?.includes(p))
    },
    [currentPath, label],
  )

  // For desktop with sophisticated hover dropdown
  if (hasDropdown) {
    return (
      <div
        className="hidden sm:block relative group"
        onMouseEnter={() => setIsDropdownOpen(true)}
        onMouseLeave={() => setIsDropdownOpen(false)}
      >
        <button
          onClick={() => {
            router.push(navLinkProps.to)
          }}
          className={clsx(
            'font-funnel font-medium hover:font-bold flex select-none items-center justify-center gap-x-1 transition-all duration-300 ease-out',
            {
              'text-lg font-medium hover:text-cw-midnight-purple border-b border-b-2 border-b-cw-eucal-green':
                shouldBeHighlighted(activeOn.some((p) => currentPath?.includes(p))),
              'text-lg hover:text-cw-midnight-purple': !shouldBeHighlighted(
                activeOn.some((p) => currentPath?.includes(p)),
              ),
              'pointer-events-none': isDisabled,
            },
          )}
          style={textColor ? { color: textColor } : undefined}
        >
          {label}
          <ArrowRight2LightIcon
            className={clsx('w-3 h-3 transition-all duration-300 ease-out rotate-90', {
              'rotate-[270deg]': isDropdownOpen,
            })}
          />
        </button>

        <div
          className={clsx(
            'absolute top-full left-1/2 -translate-x-1/2 transform mt-2 w-64 bg-white backdrop-blur-md border border-gray-200 rounded-xl transition-all duration-300 ease-out z-50',
            {
              'opacity-100 visible translate-y-0': isDropdownOpen,
              'opacity-0 invisible -translate-y-2': !isDropdownOpen,
            },
          )}
        >
          <div className="p-5">
            <div className="flex flex-col gap-2">
              {subItems.map((item, index) => (
                <React.Fragment key={item.label}>
                  <NavLink
                    to={item.navLinkProps.to}
                    onClick={() => setIsDropdownOpen(false)}
                    className={
                      'text-center flex flex-col p-3 rounded-lg hover:bg-cw-purple-light-hov/80 transition-all duration-200'
                    }
                  >
                    {() => (
                      <span className="text-base font-medium font-funnel text-cw-midnight-purple">
                        {item.label}
                      </span>
                    )}
                  </NavLink>
                  {index !== subItems.length - 1 && <hr className="border-gray-200" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Standard navigation item (mobile or no dropdown)
  return (
    <NavLink
      to={navLinkProps?.to || '#'}
      className={({ isActive }) =>
        clsx(
          'font-funnel font-medium hover:font-bold flex select-none flex-col items-center justify-end gap-y-1 transition-all',
          {
            'text-base font-medium hover:text-cw-midnight-purple border-b border-b-2 border-b-cw-eucal-green':
              shouldBeHighlighted(isActive) && !isMobile,

            'px-2 py-1 text-xs !font-light bg-cw-purple-light-hov rounded-xl':
              shouldBeHighlighted(isActive) && isMobile,

            'text-base hover:text-cw-midnight-purple':
              !shouldBeHighlighted(isActive) && !isMobile,

            'px-2 py-1 text-xs !font-light': !shouldBeHighlighted(isActive) && isMobile,

            'pointer-events-none': isDisabled,
          },
        )
      }
      style={textColor ? { color: textColor } : undefined}
    >
      {isMobile ? (
        <>
          <ResponsiveIcon
            menuIcon={true}
            path={IconPath}
            Icon={Icon}
            iconWidth={iconWidth}
            iconHeight={iconHeight}
          />
          {label}
        </>
      ) : (
        label
      )}
    </NavLink>
  )
}
